import { MessagingProvider } from '@arhs-fleetback/types/entities/messaging-accounts';
import { createSelector } from 'reselect';

import { SEARCH, SORT } from 'constants/FilterConstants';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { getMessagingAccountsByIds, getUIState } from 'modules/messagingAccounts/selectors';
import type { FetchMessagingAccountsParams, MessagingAccountFormValues, MessagingAccountOwner } from 'modules/messagingAccounts/types';
import { isGroupManagement } from 'modules/router/selectors';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps, getRoot } from 'utils/selectors';

const getDashboardState = createSelector(getUIState, ui => ui.dashboard);

const getDashboardData = createSelector(getDashboardState, dashboard => dashboard.data);

const getDashboardIds = createSelector(getDashboardData, data => data.ids);

export const getDashboardEntries = createSelector(getRoot, getDashboardIds, getMessagingAccountsByIds);

export const getDashboardPage = createSelector(getDashboardData, data => data.page);

export const isDashboardLoading = createSelector(getDashboardData, data => data.loading);

export const getDashboardFilters = createSelector(getDashboardState, dashboard => dashboard.filters);

const getDeletingIds = createSelector(getDashboardState, dashboard => dashboard.deleting);

export const isDeleting = createSelector(getDeletingIds, getProps<string>, (deletingIds, accountId) =>
  deletingIds.includes(accountId));

export const getDashboardOwner = createSelector(
  isGroupManagement,
  getSelectedGroupId,
  getSelectedDealerId,
  (isGroup, groupId, dealerId): MessagingAccountOwner => (isGroup ? { groupId } : { dealerId })
);

export const getBaseRequestParams = createSelector(
  getDashboardOwner,
  getDashboardPage,
  getDashboardFilters,
  (owner, { size }, filters): FetchMessagingAccountsParams => ({
    ...owner,
    page: 0,
    size,
    sort: filters[SORT],
    ...(filters[SEARCH] ? { name: filters[SEARCH] } : {}),
  })
);

const getModalState = createSelector(getUIState, ui => ui.modal);

export const isModalOpen = createSelector(getModalState, modal => modal.open);

export const getModalEntityId = createSelector(getModalState, modal => modal.entityId);

const getModalAccount = createSelector(getModalState, modal => modal.account);

export const isModalFetching = createSelector(getModalState, modal => modal.fetching);

export const isModalSaving = createSelector(getModalState, ({ saving }) => saving.creating || saving.updating);

/**
 * Initial values of the create/edit modal: the owner of the dashboard in create mode, the account
 * loaded by id in edit mode — its token only comes with that load, the list never carries it.
 */
export const getModalInitialValues = createSelector(
  getModalEntityId,
  getModalAccount,
  getDashboardOwner,
  (accountId, account, owner): Partial<MessagingAccountFormValues> => {
    if (!accountId) {
      return { ...owner, provider: MessagingProvider.CMCOM, sms: { phoneNumbers: [] } };
    }
    if (!account) {
      return { id: accountId, provider: MessagingProvider.CMCOM };
    }
    return {
      id: account.id,
      name: account.name,
      productToken: account.productToken,
      provider: account.provider,
      sms: { phoneNumbers: account.sms?.phoneNumbers ?? [] },
    };
  }
);
